import React from 'react';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useShopify } from '../../contexts/ShopifyContext';
import logger from '../../utils/logger';
import styles from './CartSidebar.module.css';

const CartSidebar: React.FC = () => {
  const { 
    checkout, 
    isCartOpen, 
    closeCart, 
    updateCartItem, 
    removeFromCart,
    loading 
  } = useShopify();

  const lineItems = checkout?.lineItems || [];
  const subtotal = checkout?.subtotalPrice || { amount: '0', currencyCode: 'USD' };
  
  const formatPrice = (amount: string | number, currency: string = 'USD') => {
    const value = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  const handleCheckout = () => {
    if (checkout?.webUrl) {
      // Replace buy_button channel with online_store channel
      let cleanUrl = checkout.webUrl;

      // Parse the URL and replace the channel parameter
      try {
        const url = new URL(checkout.webUrl);
        if (url.searchParams.get('channel') === 'buy_button') {
          url.searchParams.set('channel', 'online_store');
          cleanUrl = url.toString();
        }
      } catch (e) {
        logger.warn('Could not parse checkout URL');
      }

      window.location.href = cleanUrl;
    } else {
      alert('Unable to proceed to checkout. Please try refreshing the page.');
    }
  };

  const handleQuantityChange = async (lineItemId: string, currentQuantity: number, change: number) => {
    const newQuantity = currentQuantity + change;
    if (newQuantity <= 0) {
      await removeFromCart(lineItemId);
    } else {
      await updateCartItem(lineItemId, newQuantity);
    }
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className={styles.overlay}
            onClick={closeCart}
          />
          <motion.div
            initial={{ opacity: 0, transform: 'translateX(100%)' }}
            animate={{ opacity: 1, transform: 'translateX(0)' }}
            exit={{ opacity: 0, transform: 'translateX(100%)' }}
            transition={{ type: 'tween', duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className={styles.sidebar}
          >
            <div className={styles.header}>
              <h2>Shopping Bag</h2>
              <button
                onClick={closeCart}
                className={styles.closeButton}
                aria-label="Close cart"
              >
                <X size={24} />
              </button>
            </div>

            {lineItems.length === 0 ? (
              <div className={styles.emptyCart}>
                <ShoppingBag size={48} />
                <p>Your bag is empty</p>
                <button onClick={closeCart} className={styles.continueButton}>
                  Continue Shopping
                </button>
              </div>
            ) : (
              <>
                <div className={styles.cartItems}>
                  {lineItems.map((item: any) => {
                    // The quantity should already be correct from context's checkoutWithLocalQuantities
                    const itemQuantity = item.quantity || 0;
                    
                    return (
                    <div key={item.id} className={styles.cartItem}>
                      <div className={styles.itemImage}>
                        <img 
                          src={item.variant?.image?.src || '/placeholder-image.png'} 
                          alt={item.title}
                        />
                      </div>
                      <div className={styles.itemDetails}>
                        <h3>{item.title}</h3>
                        <p className={styles.variant}>{item.variant?.title}</p>
                        <p className={styles.itemPrice}>
                          {formatPrice(item.variant?.price?.amount || '0', item.variant?.price?.currencyCode)}
                        </p>
                        <div className={styles.quantityControl}>
                          <button
                            onClick={() => handleQuantityChange(item.id, itemQuantity, -1)}
                            disabled={loading}
                            className={styles.quantityButton}
                          >
                            <Minus size={16} />
                          </button>
                          <span className={styles.quantity}>{itemQuantity}</span>
                          <button
                            onClick={() => handleQuantityChange(item.id, itemQuantity, 1)}
                            disabled={loading}
                            className={styles.quantityButton}
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className={styles.removeButton}
                        disabled={loading}
                      >
                        <X size={18} />
                      </button>
                    </div>
                    );
                  })}
                </div>

                <div className={styles.summary}>
                  <div className={styles.summaryRow}>
                    <span>Subtotal</span>
                    <span>{formatPrice(subtotal.amount, subtotal.currencyCode)}</span>
                  </div>
                  <p className={styles.shipping}>Shipping & taxes calculated at checkout</p>
                  
                  <button 
                    onClick={handleCheckout}
                    className={styles.checkoutButton}
                    disabled={loading || !checkout?.webUrl}
                  >
                    {loading ? 'Processing...' : 'Proceed to Checkout'}
                  </button>
                  
                  <button onClick={closeCart} className={styles.continueLink}>
                    Continue Shopping
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartSidebar;